import { useEffect, useState } from 'react';
import { useIsFetching, useIsMutating } from '@tanstack/react-query';
import { useLocation } from 'react-router';

export type NexaActivity = 'idle' | 'splash' | 'compact';

/** Wait for route-level `useQuery` hooks to register before deciding splash vs cache-hit. */
export const ROUTE_SETTLE_MS = 80;

interface PathGate {
  path: string;
  settled: boolean;
  ready: boolean;
}

export function useNexaQueryActivity(): NexaActivity {
  const { pathname } = useLocation();
  const pendingWithoutData = useIsFetching({
    predicate: (query) =>
      query.getObserversCount() > 0 &&
      query.state.status === 'pending' &&
      query.state.fetchStatus === 'fetching',
  });
  const fetching = useIsFetching({
    predicate: (query) => query.getObserversCount() > 0 && query.state.fetchStatus === 'fetching',
  });
  const mutating = useIsMutating();

  const [gate, setGate] = useState<PathGate>({ path: pathname, settled: false, ready: false });

  if (gate.path !== pathname) {
    setGate({ path: pathname, settled: false, ready: false });
  }

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setGate((current) => (current.path === pathname ? { ...current, settled: true } : current));
    }, ROUTE_SETTLE_MS);
    return () => window.clearTimeout(timer);
  }, [pathname]);

  useEffect(() => {
    if (!gate.settled || gate.ready) return;
    if (pendingWithoutData === 0) {
      setGate((current) => ({ ...current, ready: true }));
    }
  }, [gate.settled, gate.ready, pendingWithoutData]);

  if (!gate.settled || gate.path !== pathname) return 'idle';
  if (!gate.ready && pendingWithoutData > 0) return 'splash';
  if (fetching > 0 || mutating > 0) return 'compact';
  return 'idle';
}
